"use client";

import { useEffect } from "react";
import { CONSENT_UPDATED_EVENT } from "@/components/ui/cookie-consent-events";
import { parseConsent, applyPostHogConsent } from "./utils";

// Bridges CookieYes's native banner to the app. CookieYes owns the consent
// UI, the cookie, and the Google Consent Mode updates (dashboard GCM
// integration); this component only reacts to its events:
// - drives the npm-bundled PostHog SDK (start on grant, opt-out + cleanup on
//   revocation) — CookieYes's script blocking can't reach bundled code;
// - re-fires CONSENT_UPDATED_EVENT so cookie readers (GtmLoader) re-check
//   without a reload.
//
// Both CookieYes events are handled (`cookieyes_consent_update` on a
// visitor's banner action, `cookieyes_banner_load` once per page load), plus
// a bounded boot poll: in non-targeted regions CookieYes renders no banner —
// it just writes the implied-grant cookie at boot, and no event is guaranteed
// to fire. Consent-gated loaders mount before the SDK boots and read
// "undecided", so without the poll a first-time visitor in a non-targeted
// region would get no analytics until their next page load.
export default function ConsentSync() {
  useEffect(() => {
    const sync = () => {
      const { consented, prefs } = parseConsent();
      if (consented) {
        applyPostHogConsent(prefs.analytics || prefs.performance);
      }
      window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT));
    };
    document.addEventListener("cookieyes_consent_update", sync);
    document.addEventListener("cookieyes_banner_load", sync);

    // Boot poll: fire one sync as soon as a decided state exists (10s cap —
    // SDK blocked or localhost means there is never anything to sync).
    let attempts = 40;
    let timer: number | undefined;
    const poll = () => {
      if (parseConsent().consented) {
        sync();
        return;
      }
      attempts -= 1;
      if (attempts > 0) timer = window.setTimeout(poll, 250);
    };
    poll();

    return () => {
      document.removeEventListener("cookieyes_consent_update", sync);
      document.removeEventListener("cookieyes_banner_load", sync);
      window.clearTimeout(timer);
    };
  }, []);

  return null;
}
